import { useCallback, useMemo } from 'react';
import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { PlayFill } from '@lyearn/icons';
import { Button, IconButton, Stack } from '@lyearn/ui';
import PublishChangesPopover from '@/components/EditorBuilder/Settings/Dialogs/PublishChanges/PublishChangesPopover';
import WorkspaceAccessControlDialog from '@/components/WorkspaceAccessControl/components/WorkspaceAccessControlDialog';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useSubscribeToJobUpdates from '@/modules/corporate/modules/article/views/ArticleBuilder/components/Header/hooks/useSubscribeToJobUpdates';
import { useLocalizedContentsQuery } from '@/modules/corporate/modules/course/graphql/queries/generated/localizedContents';
import LanguageSelector from '@/modules/corporate/modules/course/views/CourseBuilder/components/LanguageSelector';
import {
  PathInfoMultilingualFragment,
  usePathMultilingualInfoQuery,
} from '@/modules/corporate/modules/path/views/PathEditor/graphql/queries/generated/pathMultilingualInfo';
import SavingState from '@/modules/corporate/modules/quiz/views/QuizEditor/components/QuizEditorHeader/components/SavingState';
import { ResourceStatusEnum, ResourceTypeEnum, WorkspaceActions } from '@/types/schema';

import { HeaderProps } from '../../types';
import DefaultLangaugeSelector from './defaultLangaugeSelector';

type Props = Pick<
  HeaderProps,
  | 'disabled'
  | 'isArchived'
  | 'isEditMode'
  | 'settings'
  | 'status'
  | 'workspaceSubject'
  | 'onSaveDraft'
  | 'onSaveDraftAndPreview'
  | 'onSubmit'
  | 'publishProps'
  | 'contentType'
  | 'showSavingState'
  | 'saving'
  | 'workspaceAction'
  | 'id'
  | 'allowMinorVersionPublish'
  | 'path'
  | 'setDefaultLanguageType'
  | 'defaultLanguageType'
>;

const EditorHeaderRightNode = (props: Props) => {
  const { t } = useTranslation();
  const {
    disabled = false,
    isArchived = false,
    isEditMode = false,
    showSavingState,
    saving,
    status,
    settings,
    workspaceSubject,
    onSaveDraft,
    onSaveDraftAndPreview,
    onSubmit,
    publishProps,
    contentType,
    workspaceAction,
    id,
    allowMinorVersionPublish,
    path,
    setDefaultLanguageType,
    defaultLanguageType,
  } = props;

  const showSaveDraft = onSaveDraft && workspaceSubject;
  const showSaveDraftAndPreview = onSaveDraftAndPreview && workspaceSubject;
  const showPublish = isArchived || publishProps?.shouldPublish;
  const { [FeatureFlags.EnableMultiLanguageContent]: isMultiLanguageContentEnabled } =
    useFeatureFlags();

  const [{ data: pathInfo }, refetchPathInfo] = usePathMultilingualInfoQuery({
    variables: {
      pathId: id!,
    },
    requestPolicy: 'network-only',
    pause: !id,
  });

  const pathMultilingualInfo = (pathInfo?.content ?? {}) as PathInfoMultilingualFragment;
  const { languageConfigs, isLocalizedContent } = pathMultilingualInfo;

  const localizedContentIds = languageConfigs?.map((config) => config.contentId) || [];
  const [localizedContents, refetchLocalizedContent] = useLocalizedContentsQuery({
    variables: { filters: { ids: localizedContentIds } },
    pause:
      !localizedContentIds?.length ||
      isLocalizedContent ||
      path?.status == ResourceStatusEnum.Draft,
    requestPolicy: 'network-only',
  });
  const { jobStatus } = useSubscribeToJobUpdates({ languageConfigs });
  const isMainContentInDraftState = !isLocalizedContent && path?.status == ResourceStatusEnum.Draft;
  const actionButtonText = useMemo(() => {
    if (showPublish) {
      return t('Common:Actions.Publish');
    }
    if (isEditMode) {
      return t('Common:Actions.Update');
    }
    return t('Common:Actions.Create');
  }, [isEditMode, showPublish, t]);

  const renderSaveAsDraft = useCallback(
    (onClick?: (values: any) => void) => (
      <Button
        appearance="secondary"
        data-action="SAVE_AS_DRAFT"
        disabled={disabled}
        variant="outlined"
        onClick={onClick}>
        {t('Common:Actions.SaveAsDraft')}
      </Button>
    ),
    [disabled, t],
  );

  const renderSaveAsDraftAndPreview = useCallback(
    (onClick?: (values: any) => void) => (
      <IconButton
        Icon={PlayFill}
        appearance="primary"
        data-action="SAVE_AS_DRAFT_AND_PREVIEW"
        disabled={disabled}
        size="xl"
        variant="outlined"
        onClick={onClick}
      />
    ),
    [disabled],
  );

  const renderActionButton = useCallback(
    (onClick?: () => void) => (
      <Button
        data-action={showPublish ? 'PUBLISH' : isEditMode ? 'UPDATE' : 'CREATE'}
        disabled={disabled}
        onClick={onClick}>
        {actionButtonText}
      </Button>
    ),
    [actionButtonText, disabled, isEditMode, showPublish],
  );

  const renderSubmit = useCallback(
    (onClick?: () => void) => {
      if (showPublish && publishProps) {
        const { hidePublishPopover, onPublish, isPublishPopoverVisible = false } = publishProps;

        return (
          <PublishChangesPopover
            allowMinorVersionPublish={allowMinorVersionPublish}
            contentType={contentType}
            disabled={disabled}
            id={id}
            open={isPublishPopoverVisible}
            onClose={hidePublishPopover}
            onPublish={onPublish}>
            {renderActionButton(onClick)}
          </PublishChangesPopover>
        );
      }

      return renderActionButton(onClick);
    },
    [
      allowMinorVersionPublish,
      contentType,
      disabled,
      id,
      publishProps,
      renderActionButton,
      showPublish,
    ],
  );
  const isDefaultLanguageSelectorEnabled =
    (!path || isMainContentInDraftState) &&
    isMultiLanguageContentEnabled &&
    contentType == ResourceTypeEnum.Path;
  contentType == ResourceTypeEnum.Path;

  const isLangaugeSelectorDisabled =
    !path ||
    isMainContentInDraftState ||
    !isMultiLanguageContentEnabled ||
    contentType !== ResourceTypeEnum.Path;
  return (
    <Stack alignItems="center" gapX={8}>
      {isLangaugeSelectorDisabled ? null : (
        <LanguageSelector
          className="mr-8"
          content={pathMultilingualInfo}
          jobStatus={jobStatus}
          localizedContents={localizedContents?.data}
          refetchLocalizedContent={refetchLocalizedContent}
          refetchQuery={refetchPathInfo}
        />
      )}
      {isDefaultLanguageSelectorEnabled ? (
        <DefaultLangaugeSelector
          defaultLanguageType={defaultLanguageType}
          setDefaultLanguageType={setDefaultLanguageType}
        />
      ) : null}
      {settings ? settings : null}
      {showSavingState ? (
        <Stack alignItems="center" className="mx-6">
          <SavingState isSaving={!!saving} />
          {/* <Divider className="h-28 w-1 bg-bg-tertiary ms-12" /> */}
        </Stack>
      ) : null}
      {showSaveDraftAndPreview ? (
        <WorkspaceAccessControlDialog
          action={status ? WorkspaceActions.Edit : WorkspaceActions.Create}
          fallback={renderSaveAsDraftAndPreview()}
          subject={workspaceSubject}>
          {renderSaveAsDraftAndPreview(onSaveDraftAndPreview)}
        </WorkspaceAccessControlDialog>
      ) : null}
      {showSaveDraft ? (
        <WorkspaceAccessControlDialog
          action={status ? WorkspaceActions.Edit : WorkspaceActions.Create}
          fallback={renderSaveAsDraft()}
          subject={workspaceSubject}>
          {renderSaveAsDraft(onSaveDraft)}
        </WorkspaceAccessControlDialog>
      ) : null}
      {workspaceSubject ? (
        <WorkspaceAccessControlDialog
          action={
            workspaceAction
              ? workspaceAction
              : showPublish
              ? WorkspaceActions.Publish
              : isEditMode
              ? WorkspaceActions.Edit
              : WorkspaceActions.Create
          }
          fallback={renderSubmit()}
          subject={workspaceSubject}>
          {renderSubmit(onSubmit)}
        </WorkspaceAccessControlDialog>
      ) : (
        renderSubmit(onSubmit)
      )}
    </Stack>
  );
};

export default React.memo(EditorHeaderRightNode);
